import queryString from 'query-string';
import { useEffect, useReducer } from 'react';
import { getDefaultSearchDateAndTime } from '../utils/Date';
import { getHours, getMinutes, parseISO, set } from 'date-fns';
import OrderType from '../enums/OrderType';
import { FilterAreaType, FilterItemType, getFiltersInUse } from '../api/filter.api';
import Axios from 'axios';
import { useHistory } from 'react-router-dom';

/*
 * Search Criteria related to bookings
 */

export type SearchCriteriaStoreState = {
  guests: number;
  dateTime: Date;
  filters: FilterItemType[];
  allFilters: {
    occasions: FilterItemType[];
    categories: FilterItemType[];
    cities: FilterItemType[][];
    areas: FilterAreaType[];
  };
};

export type SearchCriteriaStore = {
  reducer: [SearchCriteriaStoreState, React.Dispatch<SearchCriteriaAction>];
  urlifyFilters: (filters: FilterItemType[]) => string;
};

export type SearchCriteriaAction =
  | { type: 'Guests'; payload: number }
  | { type: 'DateTime'; payload: Date }
  | { type: 'Date'; payload: Date }
  | { type: 'Time'; payload: Date }
  | { type: 'OrderType'; payload: OrderType }
  | { type: 'LocationId'; payload: number }
  | { type: 'Filters'; payload: FilterItemType[] }
  | {
      type: 'AllFilters';
      payload: {
        occasions: FilterItemType[];
        categories: FilterItemType[];
        cities: FilterItemType[][];
        areas: FilterAreaType[];
      };
    };

const parseFilters = (filtersStr: string, allFilters: FilterItemType[]): FilterItemType[] => {
  return filtersStr.split(',').map((f) => {
    const split = f.split(';');
    const id = parseInt(split[0], 10);
    const type = parseInt(split[1], 10);
    const name = allFilters.find((x) => x.id === id && x.type === type);

    return {
      id,
      type,
      name: name?.name || '',
    };
  });
};

export const useSearchCriteriaStore = () => {
  const history = useHistory();

  const {
    guests: qsGuests,
    dateTime: qsDateTime,
    filters: qsFilters,
  }: {
    guests?: string;
    dateTime?: string;
    orderType?: string;
    filters?: string;
  } = queryString.parse(history.location.search);

  const initState: SearchCriteriaStoreState = {
    guests: qsGuests ? parseInt(qsGuests, 10) : 2,
    dateTime: qsDateTime ? parseISO(qsDateTime) : getDefaultSearchDateAndTime(new Date()),
    filters: qsFilters ? parseFilters(qsFilters, []) : [],
    allFilters: {
      categories: [],
      cities: [],
      occasions: [],
      areas: [],
    },
  };

  const storeReducer = (
    storeState: SearchCriteriaStoreState,
    action: SearchCriteriaAction,
  ): SearchCriteriaStoreState => {
    switch (action.type) {
      case 'Guests':
        return { ...storeState, guests: action.payload };
      case 'DateTime':
        return { ...storeState, dateTime: set(action.payload, { seconds: 0, milliseconds: 0 }) };
      case 'Date': {
        return {
          ...storeState,
          dateTime: set(new Date(action.payload), {
            hours: getHours(storeState.dateTime),
            minutes: getMinutes(storeState.dateTime),
            seconds: 0,
            milliseconds: 0,
          }),
        };
      }
      case 'Time': {
        return {
          ...storeState,
          dateTime: set(new Date(storeState.dateTime), {
            hours: getHours(action.payload),
            minutes: getMinutes(action.payload),
            seconds: 0,
            milliseconds: 0,
          }),
        };
      }
      case 'Filters':
        return { ...storeState, filters: action.payload };
      case 'AllFilters':
        return { ...storeState, allFilters: action.payload };
      default:
        throw new Error('Could not find state type in search criteria store');
    }
  };

  const reducer = useReducer(storeReducer, initState);
  const [s, dispatch] = reducer;

  useEffect(() => {
    const ct = Axios.CancelToken.source();

    getFiltersInUse(ct)
      .then((res) => dispatch({ type: 'AllFilters', payload: res.data }))
      .catch((e) => {
        if (!Axios.isCancel(e)) {
          console.error('Error fetching categories', e);
        }
      });

    return () => ct.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const flat: FilterItemType[] = [
      ...s.allFilters.categories,
      ...s.allFilters.occasions,
      ...s.allFilters.cities.reduce((a, b) => [...a, ...b], []),
    ];

    dispatch({ type: 'Filters', payload: qsFilters ? parseFilters(qsFilters, flat) : [] });
  }, [dispatch, qsFilters, s.allFilters]);

  const urlifyFilters = (filters: FilterItemType[]) => {
    return filters.map((f) => `${f.id};${f.type}`).join(',');
  };

  return { reducer, urlifyFilters };
};
